import cardValidator from 'card-validator';
import {
  types as CardType,
} from 'credit-card-type';
import visa from 'payment-icons/min/single/visa.svg';
import mastercard from 'payment-icons/min/single/mastercard.svg';
import amex from 'payment-icons/min/single/amex.svg';
import discover from 'payment-icons/min/single/discover.svg';
import diners from 'payment-icons/min/single/diners.svg';
import jcb from 'payment-icons/min/single/jcb.svg';
import unionpay from 'payment-icons/min/single/unionpay.svg';
import unknown from 'payment-icons/min/mono/default.svg';
import { checkStatus } from '../utils/helpers';
import config from '../../config';

export const DEFAULT_ICON = unknown;
export const INVALID_CARD = 'Your card number is invalid.';
export const INVALID_EXPIRY = "Your card's expiration is invalid.";
export const INVALID_CVC = "Your card's security code is incomplete.";

const DEFAULT_CARD = {
  gaps: [4, 8, 12],
  lengths: [16],
  code: { size: 3 },
};

class PayFac {

  brandIcon = (card) => {
    if (!card) return DEFAULT_ICON;
    switch (card.type) {
      case CardType.VISA:
        return visa;
      case CardType.MASTERCARD:
        return mastercard;
      case CardType.AMERICAN_EXPRESS:
        return amex;
      case CardType.DINERS_CLUB:
        return diners;
      case CardType.DISCOVER:
        return discover;
      case CardType.JCB:
        return jcb;
      case CardType.UNIONPAY:
        return unionpay;
      default:
        return DEFAULT_ICON;
    }
  }

  prettyCardNumber = (cardNumber, card) => {
    if (card) {
      const offsets = [].concat(0, card.gaps, cardNumber.length);
      const components = [];

      for (let i = 0; offsets[i] < cardNumber.length; i += 1) {
        const start = offsets[i];
        const end = Math.min(offsets[i + 1], cardNumber.length);
        components.push(cardNumber.substring(start, end));
      }

      return components.join(' ');
    }

    return cardNumber;
  }

  formatCardNumber(cardNumber, card) {
    const maxLength = card.lengths[card.lengths.length - 1];
    const limitCard = cardNumber.substr(0, maxLength);
    return this.prettyCardNumber(limitCard, card);
  }

  validateCardNumber(cardNumber, checkPotentiallyValid = false) {
    const sanitized = cardNumber.replace(/[^\d]/g, '');
    const validation = cardValidator.number(sanitized);
    const card = validation.card || DEFAULT_CARD;
    validation.brandIcon = this.brandIcon(validation.card);
    validation.formatted = this.formatCardNumber(sanitized, card);

    if (checkPotentiallyValid) {
      validation.error = !validation.isPotentiallyValid ? INVALID_CARD : null;
    } else {
      validation.error = !validation.isValid ? INVALID_CARD : null;
    }

    return validation;
  }

  formatExpiry = (expiry) => {
    const limitExpiry = expiry.substr(0, 4);

    if (limitExpiry.match(/^[2-9]$/)) {
      return `0${limitExpiry}`;
    }

    if (limitExpiry.length > 2) {
      return [
        limitExpiry.substr(0, 2),
        limitExpiry.substr(2, limitExpiry.length),
      ].join(' / ');
    }

    return limitExpiry;
  };

  validateCardExpiry = (expiry, checkPotentiallyValid = false) => {
    const sanitized = expiry.replace(/[^\d]/g, '');
    const formatted = this.formatExpiry(sanitized);
    const validation = cardValidator.expirationDate(formatted);
    validation.formatted = formatted;

    if (checkPotentiallyValid) {
      validation.error = !validation.isPotentiallyValid ? INVALID_EXPIRY : null;
    } else {
      validation.error = !validation.isValid ? INVALID_EXPIRY : null;
    }

    return validation;
  }

  formatCardCvc = (cvc, card) => cvc.substr(0, card.code.size);

  validateCardCvc = (cvc, cardNumber, checkPotentiallyValid = false) => {
    const sanitizedCvc = cvc.replace(/[^\d]/g, '');
    const sanitizedNumber = cardNumber.replace(/[^\d]/g, '');
    const validation = cardValidator.number(sanitizedNumber);
    const card = validation.card || DEFAULT_CARD;
    const formatted = this.formatCardCvc(sanitizedCvc, card);
    validation.formattedCvc = formatted;
    validation.cvcError = checkPotentiallyValid ||
      (!checkPotentiallyValid &&
        formatted && formatted.length === card.code.size) ?
      null : INVALID_CVC;
    return validation;
  };

  createToken = ({
    cardNumber,
    cvc,
    expiry,
    zipCode,
  }) => {
    const url = `${config.squireTokensUrl}/tokens`;
    const expiryValidation = this.validateCardExpiry(expiry);
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        number: cardNumber,
        expMonth: expiryValidation.month,
        expYear: expiryValidation.year,
        cvc,
        zipCode,
      }),
    }).then(checkStatus);
  }
}

export default PayFac;



// WEBPACK FOOTER //
// ./src/app/utils/payFac.js